import { type DicomJson, type InstanceRef, rerootBulkDataUri, type SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { timedFetch } from '../net';

/**
 * Byte retrieval for non-image content, through the same DICOMweb root and headers (viewer ticket)
 * as the images: WADO-RS bulk data, rendered media and whole-instance retrieve (parsed with the
 * dicom-parser already loaded for the image loader).
 */

/** Any stored transfer syntax: without it a PACS must transcode, which fails for video (MPEG) streams. */
export const PART10_ACCEPT = 'multipart/related; type="application/dicom"; transfer-syntax=*';
const BULK_ACCEPT = 'multipart/related; type="application/octet-stream"';
const CRLF_CRLF = [13, 10, 13, 10];

export interface Fetched {
  bytes: Uint8Array;
  contentType: string;
}

/** Minimal dicom-parser surface used here (the UMD global is untyped). */
export interface DicomElement {
  dataOffset: number;
  length: number;
  fragments?: Array<{ offset: number; position: number; length: number }>;
  items?: Array<{ dataSet: DicomDataSet }>;
}

export interface DicomDataSet {
  byteArray: Uint8Array;
  elements: Record<string, DicomElement | undefined>;
  string(tag: string): string | undefined;
  uint32(tag: string): number | undefined;
}

interface DicomParserLib {
  parseDicom(bytes: Uint8Array, options?: Record<string, unknown>): DicomDataSet;
  readEncapsulatedPixelDataFromFragments(dataSet: DicomDataSet, element: DicomElement, startFragment: number, numFragments?: number): Uint8Array;
}

function parser(): DicomParserLib {
  return window.dicomParser as DicomParserLib;
}

/** GET with the viewer headers; throws on any non-2xx answer. */
export async function fetchBytes(shell: ViewerShell, url: string, accept: string): Promise<Fetched> {
  const response = await timedFetch(url, { headers: { Accept: accept, ...shell.ctx.client.requestHeaders() }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`DICOMweb ${response.status}`);
  return { bytes: new Uint8Array(await response.arrayBuffer()), contentType: response.headers.get('Content-Type') ?? '' };
}

function indexOf(haystack: Uint8Array, needle: ArrayLike<number>, from = 0): number {
  const first = needle[0];
  outer: for (let i = from; i <= haystack.length - needle.length; i++) {
    if (haystack[i] !== first) continue;
    for (let j = 1; j < needle.length; j++) if (haystack[i + j] !== needle[j]) continue outer;
    return i;
  }
  return -1;
}

/** Body of the first part of a multipart/related answer, or the whole body when it is not multipart. */
export function firstPart(fetched: Fetched): Uint8Array {
  const match = /boundary="?([^";]+)"?/i.exec(fetched.contentType);
  if (!/^multipart\//i.test(fetched.contentType) || !match) return fetched.bytes;
  const delimiter = new TextEncoder().encode(`--${match[1]}`);
  const start = indexOf(fetched.bytes, delimiter);
  if (start < 0) throw new Error('multipart: no boundary');
  const headersEnd = indexOf(fetched.bytes, CRLF_CRLF, start + delimiter.length);
  if (headersEnd < 0) throw new Error('multipart: no part headers');
  const bodyStart = headersEnd + CRLF_CRLF.length;
  const next = indexOf(fetched.bytes, new TextEncoder().encode(`\r\n--${match[1]}`), bodyStart);
  return fetched.bytes.subarray(bodyStart, next < 0 ? fetched.bytes.length : next);
}

/**
 * A metadata BulkDataURI re-rooted on the viewer's DICOMweb root: PACS answer with their own internal
 * host (e.g. `http://pacs:8042/dicom-web/studies/…`), which the browser cannot and must not reach.
 */
export function bulkDataUrl(shell: ViewerShell, metadata: DicomJson | undefined, tag: string): string | undefined {
  const uri = (metadata?.[tag] as { BulkDataURI?: string } | undefined)?.BulkDataURI;
  return uri ? rerootBulkDataUri(shell.config.dicomwebRoot, uri) : undefined;
}

/** Bulk data of one attribute (first multipart part), or undefined when the metadata has no BulkDataURI. */
export async function fetchBulk(shell: ViewerShell, metadata: DicomJson | undefined, tag: string): Promise<Uint8Array | undefined> {
  const url = bulkDataUrl(shell, metadata, tag);
  if (!url) return undefined;
  return firstPart(await fetchBytes(shell, url, BULK_ACCEPT));
}

/** The whole Part-10 object (WADO-RS instance retrieve), parsed. */
export async function retrieveInstance(shell: ViewerShell, series: SeriesRef, instance: InstanceRef): Promise<DicomDataSet> {
  const fetched = await fetchBytes(shell, shell.ctx.client.instanceUrl(series, instance), PART10_ACCEPT);
  return parser().parseDicom(firstPart(fetched));
}

/** Raw value bytes of a (non-encapsulated) element. */
export function elementBytes(dataSet: DicomDataSet, tag: string): Uint8Array | undefined {
  const element = dataSet.elements[tag];
  if (!element) return undefined;
  return dataSet.byteArray.subarray(element.dataOffset, element.dataOffset + element.length);
}

/** Every fragment of encapsulated Pixel Data joined (a video stream is one frame split into fragments). */
export function encapsulatedPixelData(dataSet: DicomDataSet): Uint8Array | undefined {
  const element = dataSet.elements.x7fe00010;
  if (!element?.fragments?.length) return undefined;
  return parser().readEncapsulatedPixelDataFromFragments(dataSet, element, 0, element.fragments.length);
}

/** Starts a browser download of in-memory bytes (no navigation, no popup). */
export function downloadBytes(bytes: Uint8Array, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
